/**
 * Decision logic for the Sidecar-first upload (PLAT-11697), kept pure so the
 * ordering rules are unit-testable without React or S3:
 *
 * - The demux starts at file-select, in parallel with validation and the
 *   video upload. The Sidecar is uploaded only once the upload context is
 *   ready (validation passed, blank story created, credentials available).
 * - Give up if the video upload passes {@link SIDECAR_GIVE_UP_PERCENT} before
 *   the Sidecar is ready: the Original will land first anyway.
 * - Give up if the Original lands before the Sidecar was sent.
 * - Processing starts on whichever lands first — the Sidecar (with
 *   `audioSidecarKey`) or the Original (today's call) — and only once.
 */
import type { SidecarSkipReason } from './types';

export const SIDECAR_GIVE_UP_PERCENT = 50;

export type SidecarDemuxState = 'pending' | 'ready' | 'skipped';

export interface SidecarPlanState {
  /** Flag on at file-select and the upload is a single local file. */
  enabled: boolean;
  demux: SidecarDemuxState;
  /** Validation passed, blank story exists, auth token fetched. */
  uploadReady: boolean;
  /** Highest video upload progress seen, 0–100. */
  videoPercent: number;
  videoLanded: boolean;
  sidecarUploadStarted: boolean;
  sidecarLanded: boolean;
  givenUp: boolean;
}

export function initialSidecarPlanState(): SidecarPlanState {
  return {
    enabled: false,
    demux: 'pending',
    uploadReady: false,
    videoPercent: 0,
    videoLanded: false,
    sidecarUploadStarted: false,
    sidecarLanded: false,
    givenUp: false,
  };
}

export type SidecarAction =
  | { type: 'none' }
  | { type: 'wait' }
  | { type: 'upload' }
  | { type: 'give-up'; reason: SidecarSkipReason };

/** What the orchestration should do next given the current state. */
export function nextSidecarAction(state: SidecarPlanState): SidecarAction {
  if (
    !state.enabled ||
    state.givenUp ||
    state.sidecarLanded ||
    state.sidecarUploadStarted ||
    state.demux === 'skipped'
  ) {
    return { type: 'none' };
  }
  if (state.videoLanded) {
    return { type: 'give-up', reason: 'original-landed-first' };
  }
  if (state.demux === 'pending') {
    return state.videoPercent >= SIDECAR_GIVE_UP_PERCENT
      ? { type: 'give-up', reason: 'video-ahead' }
      : { type: 'wait' };
  }
  return state.uploadReady ? { type: 'upload' } : { type: 'wait' };
}

export type ProcessingStart =
  | { source: 'sidecar'; audioSidecarKey: string }
  | { source: 'original' }
  | null;

/**
 * Whether to POST `/api/story/upload-task` now, and on which object. The
 * Sidecar key is sent only when the Sidecar landed and the Original has not:
 * once the Original is there, today's call is strictly better.
 */
export function decideProcessingStart(args: {
  processingStarted: boolean;
  storyReady: boolean;
  sidecarLanded: boolean;
  audioSidecarKey: string | null;
  originalLanded: boolean;
}): ProcessingStart {
  if (args.processingStarted || !args.storyReady) return null;
  if (args.originalLanded) return { source: 'original' };
  if (args.sidecarLanded && args.audioSidecarKey) {
    return { source: 'sidecar', audioSidecarKey: args.audioSidecarKey };
  }
  return null;
}
